/** @prettier */
import React from "react";
import { MenuItem, Nav, NavDropdown, NavItem } from "react-bootstrap";
import {
    countForFilter,
    TestReviewDispatch,
    TestReviewFilter,
    testReviewFilters,
    TestReviewLoadedState,
} from "./testReviewState";

const calcPercentage = (count: number, total: number) =>
    Math.round((count * 100) / total);

export type TestReviewTitleBarProps = {
    state: TestReviewLoadedState;
    dispatch: TestReviewDispatch;
};
const labelForFilter: Record<TestReviewFilter, string> = {
    ALL: "Entire test",
    CORRECT: "Correct",
    INCORRECT: "Incorrect",
    UNANSWERED: "Unanswered",
    FLAGGED: "Flagged",
};
export const TestReviewTitleBar = ({
    state,
    dispatch,
}: TestReviewTitleBarProps) => {
    const { filterState, totalQuestionCount } = state;
    const selectFilter = (filter: TestReviewFilter) => {
        dispatch({ event: "toggleFilter", filter });
    };

    // Done once to avoid invoking `countForFilter` multiple times per filter per render
    const filtersWithCount = testReviewFilters.map((filter) => ({
        filter,
        count: countForFilter(state, filter),
    }));

    const correctAnswerCount = filtersWithCount.find(
        ({ filter }) => filter === "CORRECT",
    )!.count;
    const correctAnswerPercent = calcPercentage(
        correctAnswerCount,
        totalQuestionCount,
    );

    // Props for <NavItem> and <MenuItem> components
    const listItemProps = filtersWithCount.map(({ filter, count }) => ({
        key: filter,
        eventKey: filter,
        disabled: count === 0,
        children: `${labelForFilter[filter]} (${count})`,
    }));

    const dropdownTitle = listItemProps.find(({ key }) => key === filterState)
        ?.children;

    return (
        <div className="tr-title-bar review">
            {/* span to group the title and filters together */}
            <span className="tr-title-and-filters">
                <span className="tr-title">{state.testName}</span>
                <Nav
                    className="tr-filters hidden-xs hidden-sm"
                    id="tr-filters-lg"
                    activeKey={filterState}
                    // bug in the types here: onSelect is typed as (Event) => void
                    // but is actually (eventKey) => void
                    onSelect={selectFilter as any}
                    bsStyle="pills"
                >
                    {listItemProps.map((props) => (
                        <NavItem {...props} />
                    ))}
                </Nav>
                {/* Responsive version of the nav that shows on smaller displays, using a dropdown */}
                <Nav
                    className="tr-filters hidden-md hidden-lg hidden-xl"
                    id="tr-filters-sm"
                    activeKey={filterState}
                    // bug in the types here: onSelect is typed as (Event) => void
                    // but is actually (eventKey) => void
                    onSelect={selectFilter as any}
                >
                    <NavDropdown id="tr-filters-dropdown" title={dropdownTitle}>
                        {listItemProps.map((props) => (
                            <MenuItem {...props} />
                        ))}
                    </NavDropdown>
                </Nav>
            </span>
            <span className="tr-results-summary">
                Test results: {correctAnswerPercent}% ({correctAnswerCount}/
                {totalQuestionCount})
            </span>
        </div>
    );
};
